import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { hl7RouteAPI, systemAPI } from '../services/api';
import {
  AlertTriangle,
  CheckCircle,
  Copy,
  ExternalLink,
  Network,
  RefreshCw,
  Route,
  Server,
  Shield,
  XCircle,
} from 'lucide-react';
import toast from 'react-hot-toast';

interface HL7RouteStatus {
  service_running: boolean;
  tunnel_up: boolean;
  public_endpoint: string;
  public_ip: string;
  public_host: string;
  protected_subnet?: string;
  protected_ip?: string;
  hl7_destination?: string;
  forward_port: number;
  target_host: string;
  target_ip: string;
  target_port: number;
  allowed_source_cidr: string;
  mirth_listener_open: boolean;
  nat_configured: boolean;
  nat_rules: string[];
  ipsec_status: string;
  timestamp: string;
}

interface SystemStatus {
  active_connections: number;
  connections: Array<{ name: string; status: string; active: boolean }>;
  system?: { uptime?: string };
}

const statusTone = (ok: boolean) => ok
  ? 'border-green-200 bg-green-50 text-green-800'
  : 'border-red-200 bg-red-50 text-red-800';

function RouteCard({ title, value, ok, detail, icon: Icon }: {
  title: string;
  value: string;
  ok: boolean;
  detail: string;
  icon: React.ElementType;
}) {
  return (
    <div className={`rounded-2xl border p-5 shadow-sm ${statusTone(ok)}`}>
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm font-medium opacity-80">{title}</p>
          <p className="mt-2 text-2xl font-bold">{value}</p>
        </div>
        <div className="rounded-xl bg-white/70 p-3">
          <Icon className="h-6 w-6" />
        </div>
      </div>
      <p className="mt-4 text-sm opacity-80">{detail}</p>
    </div>
  );
}

function copy(text: string) {
  navigator.clipboard.writeText(text);
  toast.success('Copied to clipboard');
}

export default function Dashboard() {
  const [route, setRoute] = useState<HL7RouteStatus | null>(null);
  const [system, setSystem] = useState<SystemStatus | null>(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const [routeResponse, systemResponse] = await Promise.all([
        hl7RouteAPI.getStatus(),
        systemAPI.getSystemStatus(),
      ]);
      setRoute(routeResponse.data);
      setSystem(systemResponse.data);
    } catch (error: any) {
      toast.error('Failed to load HL7 route status: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  if (loading || !route) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-blue-600" />
      </div>
    );
  }

  const allGreen = route.service_running && route.nat_configured && route.mirth_listener_open && route.tunnel_up;
  const hl7Destination = route.hl7_destination || `${route.protected_ip || '172.16.1.2'}:${route.forward_port}`;
  const internalTarget = `${route.target_host}:${route.target_port}`;

  return (
    <div className="space-y-8">
      <div className="overflow-hidden rounded-3xl p-8 text-white shadow-xl" style={{ background: 'linear-gradient(135deg, #020617 0%, #172554 55%, #0f172a 100%)' }}>
        <div className="flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <div className="mb-3 inline-flex items-center rounded-full bg-blue-400/10 px-3 py-1 text-sm text-blue-100 ring-1 ring-blue-300/30">
              <Route className="mr-2 h-4 w-4" />
              Home VPN → VPS IPsec → Internal Mirth
            </div>
            <h1 className="text-3xl font-bold tracking-tight">HL7 VPN Route Dashboard</h1>
            <p className="mt-3 max-w-3xl text-blue-100">
              This panel tracks the exact path your HL7 sender needs: IPsec on UDP 500/4500 to the VPS gateway, then private TCP {route.forward_port} to {hl7Destination}, NAT-forwarded to {internalTarget}.
            </p>
          </div>
          <div className="rounded-2xl border border-white/10 bg-white/10 p-4 backdrop-blur">
            <p className="text-sm text-blue-100">Send test HL7 through tunnel to</p>
            <div className="mt-2 flex items-center gap-3">
              <code className="rounded-lg bg-black/30 px-3 py-2 text-lg font-semibold">{hl7Destination}</code>
              <button onClick={() => copy(hl7Destination)} className="rounded-lg bg-white/15 p-2 hover:bg-white/25" title="Copy endpoint">
                <Copy className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-4">
        <RouteCard
          title="VPN Node"
          value={route.service_running ? 'Healthy' : 'Down'}
          ok={route.service_running}
          detail="StrongSwan process reachable inside the Docker VPN node."
          icon={Shield}
        />
        <RouteCard
          title="HL7 Forward"
          value={route.nat_configured ? 'Enabled' : 'Missing'}
          ok={route.nat_configured}
          detail={`tcp/${route.forward_port} → ${internalTarget}`}
          icon={Network}
        />
        <RouteCard
          title="Mirth Listener"
          value={route.mirth_listener_open ? 'Open' : 'Closed'}
          ok={route.mirth_listener_open}
          detail={route.target_ip ? `${route.target_ip}:${route.target_port}` : 'Target host not resolved'}
          icon={Server}
        />
        <RouteCard
          title="Home Tunnel"
          value={route.tunnel_up ? 'Up' : 'Down'}
          ok={route.tunnel_up}
          detail={route.tunnel_up ? 'Security association is established.' : 'Waiting for home peer config/connection.'}
          icon={route.tunnel_up ? CheckCircle : XCircle}
        />
      </div>

      {!allGreen && (
        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-5 text-amber-900">
          <div className="flex items-start gap-3">
            <AlertTriangle className="mt-0.5 h-5 w-5" />
            <div>
              <h2 className="font-semibold">Next blocking item</h2>
              <p className="mt-1 text-sm">
                {!route.mirth_listener_open
                  ? `Mirth is still not accepting TCP on ${internalTarget}. Confirm the channel is deployed and bound to 0.0.0.0:${route.target_port}.`
                  : !route.tunnel_up
                    ? 'Mirth and forwarding are ready; configure the home IPsec peer next.'
                    : 'Review diagnostics below for the remaining failed check.'}
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="rounded-2xl bg-white p-6 shadow-sm ring-1 ring-gray-200 lg:col-span-2">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">Route details</h2>
            <button onClick={load} className="inline-flex items-center rounded-lg bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700">
              <RefreshCw className="mr-2 h-4 w-4" /> Refresh
            </button>
          </div>
          <dl className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="rounded-xl bg-gray-50 p-4"><dt className="text-sm text-gray-500">Public VPN gateway only</dt><dd className="mt-1 font-medium text-gray-900">{route.public_endpoint}</dd></div>
            <div className="rounded-xl bg-gray-50 p-4"><dt className="text-sm text-gray-500">HL7 destination inside tunnel</dt><dd className="mt-1 font-medium text-gray-900">{hl7Destination}</dd></div>
            <div className="rounded-xl bg-gray-50 p-4"><dt className="text-sm text-gray-500">Internal target</dt><dd className="mt-1 font-medium text-gray-900">{internalTarget}</dd></div>
            <div className="rounded-xl bg-gray-50 p-4"><dt className="text-sm text-gray-500">Allowed source CIDR</dt><dd className="mt-1 font-medium text-gray-900">{route.allowed_source_cidr || 'Not locked down yet'}</dd></div>
            <div className="rounded-xl bg-gray-50 p-4"><dt className="text-sm text-gray-500">System uptime</dt><dd className="mt-1 font-medium text-gray-900">{system?.system?.uptime || 'Unknown'}</dd></div>
          </dl>
        </div>

        <div className="rounded-2xl bg-white p-6 shadow-sm ring-1 ring-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Setup actions</h2>
          <div className="mt-4 space-y-3">
            <Link to="/connections" className="flex items-center justify-between rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 text-blue-900 hover:bg-blue-100">
              Manage customer VPNs <ExternalLink className="h-4 w-4" />
            </Link>
            <Link to="/keys" className="flex items-center justify-between rounded-xl border border-green-200 bg-green-50 px-4 py-3 text-green-900 hover:bg-green-100">
              Generate PSK safely <ExternalLink className="h-4 w-4" />
            </Link>
            <Link to="/monitoring" className="flex items-center justify-between rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-gray-900 hover:bg-gray-100">
              Run diagnostics <ExternalLink className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </div>

      <div className="rounded-2xl bg-slate-950 p-5 text-slate-100 shadow-sm">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="font-semibold">StrongSwan status</h2>
          <span className="text-xs text-slate-400">Updated {new Date(route.timestamp).toLocaleString()}</span>
        </div>
        <pre className="max-h-64 overflow-auto whitespace-pre-wrap text-xs leading-5 text-slate-300">
          {route.ipsec_status || 'No status output'}
        </pre>
      </div>
    </div>
  );
}
